import React from "react";
import { Layout } from "@/components/layout";
import { Terminal, Code, Cpu, Sparkles } from "lucide-react";
import aboutImg from "@/assets/about.png";
import { motion } from "framer-motion";
import { AnimatedSection } from "@/components/animated-section";
import { fadeUp, stagger } from "@/lib/motion";

const PRINCIPLES = [
  { icon: Terminal, num: "01", title: "Real code, real tools", body: "Production-grade Python, modern web stacks, the same editors and APIs working engineers use. No training wheels, no block-coding toys." },
  { icon: Cpu, num: "02", title: "AI native, on purpose", body: "We treat LLMs as partners, not shortcuts. Students learn to direct AI, evaluate its output, and ship something with their own taste on top." },
  { icon: Sparkles, num: "03", title: "Builder energy", body: "We measure progress by what gets shipped. Every lesson ends with something working — a script, an app, a tiny game, a useful tool." },
  { icon: Code, num: "04", title: "Working engineers as teachers", body: "Our instructors aren't substitute teachers reading from a deck. They write software for a living and bring those problems into the room." },
];

export default function About() {
  return (
    <Layout>
      {/* HERO ─────────────────────────────────────── */}
      <section className="border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-50" aria-hidden="true" />
        <div className="container mx-auto px-4 py-24 md:py-36 relative z-10">
          <motion.div
            variants={stagger(0.1)}
            initial="hidden"
            animate="visible"
            className="max-w-5xl"
          >
            <motion.div variants={fadeUp} className="section-tag mb-6">
              [ MISSION ] · Vol. 01 / Why MainLoop
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="font-display text-[3rem] sm:text-7xl md:text-[7rem] font-bold leading-[0.95] tracking-[-0.035em] mb-10 text-balance"
            >
              We&rsquo;re building
              <br />
              the school we{" "}
              <span className="font-italic-serif font-normal text-primary">wished we had.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-xl md:text-2xl text-muted-foreground max-w-3xl leading-relaxed">
              MainLoop teaches middle and high school students the actual tools real engineers use today. No worksheets, no decade-old curriculum.{" "}
              <span className="text-foreground font-medium">Just Python, AI, and raw builder energy.</span>
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* MANIFESTO ────────────────────────────────── */}
      <section className="py-24 md:py-32 border-b border-border">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7 space-y-8 text-lg leading-relaxed text-muted-foreground">
            <div className="section-tag">[ 01 ] · The thesis</div>
            <p className="font-display text-3xl md:text-4xl font-bold leading-[1.15] tracking-tight text-foreground text-balance">
              The gap is widening — and{" "}
              <span className="font-italic-serif font-normal text-primary">teenagers are perfectly placed</span>{" "}
              to close it.
            </p>
            <p>
              While traditional schools debate how to handle ChatGPT, the best builders in the world are using it to write software ten times faster. We call this <span className="font-italic-serif text-foreground">vibecoding</span> — directing AI to write code for you while you stay in the driver&rsquo;s seat.
            </p>
            <p>
              We believe teenagers are perfectly positioned to master this shift. They don&rsquo;t have to unlearn old ways of doing things. They just need the right mental models — and an honest set of tools.
            </p>
            <p>
              At MainLoop we teach foundational Python and programmatic logic, combined with modern AI workflows. Our students don&rsquo;t just learn syntax. They learn how to <span className="text-marker text-foreground">build real applications and ship them to the world.</span>
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-xl overflow-hidden bg-muted aspect-square shadow-xl border border-border">
              {aboutImg && (
                <img
                  src={aboutImg}
                  alt="Abstract illustration representing AI and learning"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-background/90 bg-foreground/70 backdrop-blur-sm rounded-md px-3 py-2">
                <span>fig. 02 · students at work</span>
                <span>spring &lsquo;26</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES ───────────────────────────────── */}
      <section className="py-24 md:py-32 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-sm opacity-[0.06] pointer-events-none" aria-hidden="true" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mb-16">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">[ 02 ] · Our principles</div>
            <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight text-balance">
              Four things we will <span className="font-italic-serif text-secondary">never</span> compromise on.
            </h2>
          </div>

          <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 gap-px bg-background/15 border border-background/15" staggerChildren={0.1}>
            {PRINCIPLES.map(p => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.num}
                  variants={fadeUp}
                  className="bg-foreground p-8 md:p-10"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div className="w-14 h-14 bg-secondary/15 text-secondary rounded-md flex items-center justify-center">
                      <Icon className="w-7 h-7" aria-hidden="true" />
                    </div>
                    <span className="font-display text-5xl font-bold text-background/30 leading-none">{p.num}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold mb-3 tracking-tight">{p.title}</h3>
                  <p className="text-background/70 leading-relaxed">{p.body}</p>
                </motion.div>
              );
            })}
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
